// S24 回答門檻政策 /ai/guardrail — 檢索門檻治理 + 試問沙盒。
//
// 現行門檻卡(GET /settings)+ calibrate 建議門檻與 pos/neg 預覽(GET /calibrate)+
// PATCH 門檻(0–1 或清除覆蓋,二段 ConfirmDialog,成功刷新)+ 試問沙盒(POST /try,顯示
// 暫時結果、不落對話軌跡)。門檻變更=AI 域寫入 → 依 canAdminWrite 顯示/隱藏寫入控制。

import { useCallback, useEffect, useMemo, useState } from 'react';
import { canAdminWrite, useAuth } from '../../auth/AuthContext';
import { ErrorState, LoadingState } from '../../components/StateBlock';
import { ConfirmDialog } from '../_shared/ConfirmDialog';
import { actionErrorMessage } from '../_shared/actionError';
import {
  fetchCalibrate,
  fetchSettings,
  formatCsTime,
  patchThreshold,
  traceStatusLabel,
  traceStatusTone,
  tryAsk,
} from './aiOpsApi';
import type { AiCalibrate, AiSettings, AiTryResult } from './aiOpsApi';
import '../../features/_shared/shared.css';
import './aiOps.css';

export default function GuardrailPage() {
  const { role } = useAuth();
  const writable = canAdminWrite(role);

  const [settings, setSettings] = useState<AiSettings | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback((signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    fetchSettings(signal)
      .then(setSettings)
      .catch((e) => {
        if (!signal?.aborted) {
          setError(e);
          setSettings(null);
        }
      })
      .finally(() => {
        if (!signal?.aborted) setLoading(false);
      });
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    load(ac.signal);
    return () => ac.abort();
  }, [load]);

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">回答門檻政策</h1>
          <p className="page-subtitle">
            檢索門檻(防線 1)runtime 可調:低於門檻不問 LLM、直接轉人工。校調對回歸題庫算建議值;變更即時生效、寫入平台稽核。
          </p>
        </div>
        <button className="btn-ghost" onClick={() => load()} disabled={loading}>
          {loading ? '載入中…' : '重新整理'}
        </button>
      </div>

      {/* 門檻卡需要 /settings:其載入/失敗僅影響此卡,不再讓整頁(含獨立的校調與試問)一起陪葬。 */}
      {loading && settings === null && <LoadingState label="載入門檻設定…" />}
      {error != null && settings === null && <ErrorState error={error} onRetry={() => load()} />}
      {settings && <ThresholdCard settings={settings} writable={writable} onSaved={() => load()} />}

      {/* 校調卡(獨立 GET /calibrate)與試問沙盒(POST /try)各自不依賴 /settings,一律渲染、各自三態。 */}
      <CalibrateCard settings={settings} />
      <SandboxCard />
    </>
  );
}

// ── 現行門檻 + PATCH ─────────────────────────────────────────────────────────

function ThresholdCard({ settings, writable, onSaved }: { settings: AiSettings; writable: boolean; onSaved: () => void }) {
  const [draft, setDraft] = useState('');
  const [clearing, setClearing] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const draftNum = draft.trim() === '' ? null : Number(draft);
  const draftValid = draft.trim() === '' || (Number.isFinite(draftNum) && (draftNum as number) >= 0 && (draftNum as number) <= 1);

  const doSubmit = (clear: boolean) => {
    setActionError(null);
    setClearing(clear);
    setConfirm(true);
  };

  const onConfirm = () => {
    setBusy(true);
    setActionError(null);
    const value = clearing ? null : (draftNum as number);
    patchThreshold(value)
      .then(() => {
        setConfirm(false);
        setDraft('');
        onSaved();
      })
      .catch((e) => setActionError(actionErrorMessage(e)))
      .finally(() => setBusy(false));
  };

  return (
    <section className="card" data-testid="guardrail-threshold">
      <div className="section-head">
        <h2 className="section-title">現行門檻</h2>
        <span className="section-meta">retrieve_threshold</span>
      </div>

      <div className="aiops-thresh-grid">
        <div className="aiops-thresh-stat">
          <span className="aiops-thresh-num">{settings.retrieve_threshold.toFixed(3)}</span>
          <span className="aiops-thresh-sub">
            {settings.is_override ? <span className="pill pill-info">覆蓋值</span> : <span className="pill pill-neutral">config 預設</span>}
          </span>
        </div>
        <div className="aiops-thresh-stat">
          <span className="aiops-meta-k">設定預設</span>
          <span className="aiops-meta-v">{settings.default.toFixed(3)}</span>
        </div>
        <div className="aiops-thresh-stat">
          <span className="aiops-meta-k">最後更新</span>
          <span className="aiops-meta-v">{settings.updated_at ? formatCsTime(settings.updated_at) : '—'}</span>
        </div>
      </div>

      {writable ? (
        <div className="aiops-reply-box" style={{ marginTop: 'var(--spacing-4)' }}>
          <label className="adm-field">
            <span className="text-secondary adm-field-label">設定覆蓋值（0–1;留空 → 清除覆蓋回預設）</span>
            <input
              type="number"
              min={0}
              max={1}
              step={0.01}
              inputMode="decimal"
              placeholder={`目前 ${settings.retrieve_threshold.toFixed(3)}`}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              aria-invalid={!draftValid}
            />
          </label>
          {!draftValid && <p className="error-text" style={{ margin: 0 }}>門檻須為 0 到 1 之間的數字。</p>}
          <div className="aiops-actions-row">
            <button className="btn-primary" disabled={draft.trim() === '' || !draftValid} onClick={() => doSubmit(false)}>
              套用門檻
            </button>
            {settings.is_override && (
              <button className="btn-ghost-warn" onClick={() => doSubmit(true)}>
                清除覆蓋（回預設）
              </button>
            )}
          </div>
        </div>
      ) : (
        <p className="aiops-hint" style={{ marginTop: 'var(--spacing-3)' }}>唯讀角色:僅檢視門檻,無法變更。</p>
      )}

      {confirm && (
        <ConfirmDialog
          title={clearing ? '清除門檻覆蓋' : '變更檢索門檻'}
          tone={clearing ? 'warn' : 'primary'}
          confirmLabel={clearing ? '清除覆蓋' : '確認套用'}
          busy={busy}
          error={actionError}
          warning={
            clearing
              ? `將移除覆蓋值,門檻回到 config 預設 ${settings.default.toFixed(3)}。即時生效。`
              : `門檻將由 ${settings.retrieve_threshold.toFixed(3)} 改為 ${(draftNum as number).toFixed(3)}。即時生效,影響所有客服對話的轉人工判定。`
          }
          body={<span>此變更會立即影響線上客服的答/不答分界,並寫入平台稽核軌。確定要繼續嗎？</span>}
          onConfirm={onConfirm}
          onCancel={() => {
            if (!busy) {
              setConfirm(false);
              setActionError(null);
            }
          }}
        />
      )}
    </section>
  );
}

// ── calibrate 建議門檻 + pos/neg 預覽 ────────────────────────────────────────

function CalibrateCard({ settings }: { settings: AiSettings | null }) {
  const [calib, setCalib] = useState<AiCalibrate | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState('');

  const run = useCallback((signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    fetchCalibrate(signal)
      .then(setCalib)
      .catch((e) => {
        if (!signal?.aborted) {
          setError(e);
          setCalib(null);
        }
      })
      .finally(() => {
        if (!signal?.aborted) setLoading(false);
      });
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    run(ac.signal);
    return () => ac.abort();
  }, [run]);

  // 預覽門檻:輸入有效值則用之,否則用現行門檻。settings 未載入(GET /settings 失敗)時
  // 基準降級為建議門檻→0,校調分析仍可用,不因門檻設定不可得而整卡消失。
  const baseline = settings?.retrieve_threshold ?? calib?.suggested ?? 0;
  const previewNum = preview.trim() === '' ? baseline : Number(preview);
  const t = Number.isFinite(previewNum) && previewNum >= 0 && previewNum <= 1 ? previewNum : baseline;

  const missAnswer = useMemo(() => (calib ? calib.pos.filter((s) => s < t).length : 0), [calib, t]);
  const falsePass = useMemo(() => (calib ? calib.neg.filter((s) => s >= t).length : 0), [calib, t]);

  return (
    <section className="card" data-testid="guardrail-calibrate">
      <div className="section-head">
        <h2 className="section-title">門檻校調</h2>
        <span className="section-meta">對回歸題庫估最佳分界</span>
        <button className="btn-ghost btn-sm" style={{ marginLeft: 'auto' }} onClick={() => run()} disabled={loading}>
          {loading ? '計算中…' : '重新計算'}
        </button>
      </div>

      {loading && calib === null && <LoadingState label="計算建議門檻…" />}
      {error != null && calib === null && <ErrorState error={error} onRetry={() => run()} />}

      {calib && (
        <>
          <div className="aiops-thresh-grid">
            <div className="aiops-thresh-stat">
              <span className="aiops-meta-k">建議門檻</span>
              <span className="aiops-thresh-num">{calib.suggested != null ? calib.suggested.toFixed(3) : '—'}</span>
              {calib.suggested == null && <span className="aiops-thresh-sub">正/負樣本不足,無法估算</span>}
            </div>
            <div className="aiops-thresh-stat">
              <span className="aiops-meta-k">題數</span>
              <span className="aiops-meta-v">{calib.n_cases}</span>
            </div>
            <div className="aiops-thresh-stat">
              <span className="aiops-meta-k">正樣本 / 負樣本</span>
              <span className="aiops-meta-v">{calib.pos.length} / {calib.neg.length}</span>
            </div>
          </div>

          <div className="aiops-calib-preview" style={{ marginTop: 'var(--spacing-4)' }}>
            <label className="adm-field" style={{ maxWidth: 320 }}>
              <span className="text-secondary adm-field-label">預覽門檻(0–1;空 = {settings ? `現行 ${settings.retrieve_threshold.toFixed(3)}` : '門檻設定未載入,以建議值為基準'})</span>
              <input type="number" min={0} max={1} step={0.01} inputMode="decimal" value={preview} onChange={(e) => setPreview(e.target.value)} />
            </label>

            <div className="aiops-calib-scale" aria-hidden="true">
              {calib.pos.map((s, i) => (
                <span key={`p${i}`} className="aiops-calib-dot is-pos" style={{ left: `${Math.max(0, Math.min(100, s * 100))}%` }} />
              ))}
              {calib.neg.map((s, i) => (
                <span key={`n${i}`} className="aiops-calib-dot is-neg" style={{ left: `${Math.max(0, Math.min(100, s * 100))}%` }} />
              ))}
              <span className="aiops-calib-line" style={{ left: `${Math.max(0, Math.min(100, t * 100))}%` }} />
            </div>

            <div className="aiops-legend">
              <span><i style={{ background: 'var(--color-semantic-success)' }} /> 應回答（正樣本）</span>
              <span><i style={{ background: 'var(--color-semantic-danger)' }} /> 不應回答（負樣本）</span>
              <span><i style={{ background: 'var(--color-brand-primary)' }} /> 門檻 {t.toFixed(3)}</span>
            </div>

            <p className="aiops-hint">
              於門檻 {t.toFixed(3)}:會誤擋(漏答)<span className="aiops-gap-count">{missAnswer}</span> 題正樣本、會誤放行 <span className="aiops-gap-count">{falsePass}</span> 題負樣本。
            </p>
          </div>
        </>
      )}
    </section>
  );
}

// ── 試問沙盒(POST /try;ephemeral) ─────────────────────────────────────────

function SandboxCard() {
  const [message, setMessage] = useState('');
  const [visibility, setVisibility] = useState<'internal' | 'public'>('internal');
  const [result, setResult] = useState<AiTryResult | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const ask = () => {
    if (!message.trim()) return;
    setBusy(true);
    setError(null);
    tryAsk(message.trim(), visibility)
      .then((r) => {
        setResult(r);
        setError(null);
      })
      .catch((e) => {
        setError(e);
        setResult(null);
      })
      .finally(() => setBusy(false));
  };

  return (
    <section className="card aiops-sandbox" data-testid="guardrail-sandbox">
      <div className="section-head">
        <h2 className="section-title">試問沙盒</h2>
        <span className="section-meta">立即驗證檢索與門檻;暫時結果,不落對話軌跡</span>
      </div>

      <div className="aiops-reply-box">
        <textarea placeholder="輸入一句問題,測試目前門檻下會答還是轉人工…" value={message} onChange={(e) => setMessage(e.target.value)} />
        <div className="aiops-actions-row">
          <label className="adm-field">
            <span className="text-secondary adm-field-label">可見度</span>
            <select value={visibility} onChange={(e) => setVisibility(e.target.value as 'internal' | 'public')}>
              <option value="internal">內部(看全部知識)</option>
              <option value="public">對外(僅對外知識)</option>
            </select>
          </label>
          <div className="adm-toolbar-spacer" />
          <button className="btn-primary" onClick={ask} disabled={busy || !message.trim()}>
            {busy ? '試問中…' : '試問'}
          </button>
        </div>
      </div>

      {error != null && <ErrorState error={error} />}

      {result && (
        <div className="aiops-block" style={{ marginTop: 'var(--spacing-4)' }}>
          <div className="aiops-actions-row">
            <span className={`pill ${traceStatusTone(result.status)}`}>{traceStatusLabel(result.status)}</span>
            <span className="aiops-meta-k">最高分 {result.top_score.toFixed(3)} · 門檻 {result.threshold.toFixed(3)}</span>
          </div>
          <p className="aiops-quote aiops-answer">{result.answer || '—'}</p>

          {result.retrieved.length > 0 && (
            <>
              <span className="aiops-block-label">檢索片段</span>
              <div className="aiops-hits">
                {result.retrieved.map((h, i) => {
                  const pct = Math.max(0, Math.min(100, Math.round((h.score ?? 0) * 100)));
                  const under = (h.score ?? 0) < result.threshold;
                  return (
                    <div className="aiops-hit" key={i}>
                      <div>
                        <div className="aiops-hit-source">{h.source || '—'}</div>
                        {h.heading_path && <div className="aiops-hit-heading">{h.heading_path}</div>}
                      </div>
                      <div className="aiops-hit-score">{h.score != null ? h.score.toFixed(3) : '—'}</div>
                      <div className={`aiops-scorebar${under ? ' is-under' : ''}`}>
                        <span style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
}
